import { useLogto } from '@logto/react';
import { useEffect, useMemo, useRef } from 'react';
import { type Api, createApi } from '../api/client';
import { LOGTO_API_RESOURCE } from '../auth/LogtoProvider';

// Module-level guard: once we've kicked off a re-auth navigation, don't fire
// it again for any in-flight requests that will also 401. The page is on its
// way out anyway.
let reauthInProgress = false;

// sessionStorage key used to remember where to return after re-auth.
const RETURN_TO_KEY = 'helloant.returnTo';

/**
 * Returns a stable Api instance. Logto's hook returns fresh function refs on
 * every render; we capture them in a ref so the api object itself never
 * changes, preventing render storms in callers that have `api` in deps.
 */
export function useApi(): Api {
  const logto = useLogto();
  const logtoRef = useRef(logto);
  useEffect(() => {
    logtoRef.current = logto;
  }, [logto]);

  return useMemo(() => {
    const triggerReauth = () => {
      if (reauthInProgress) return;
      reauthInProgress = true;
      // Preserve the user's current URL in sessionStorage so the callback
      // route can navigate them back here after re-auth.
      try {
        sessionStorage.setItem(RETURN_TO_KEY, window.location.href);
      } catch {
        // Storage may be unavailable (private mode, full quota); fall through
        // — the user will just land on '/' after re-auth.
      }
      void logtoRef.current.signIn(`${window.location.origin}/callback`);
    };

    return createApi(
      async () => {
        const { getAccessToken, isAuthenticated } = logtoRef.current;
        if (!isAuthenticated || !LOGTO_API_RESOURCE) return undefined;
        try {
          return await getAccessToken(LOGTO_API_RESOURCE);
        } catch (e) {
          // Refresh token expired or otherwise invalid. Treat as a stale
          // session and bounce to Logto.
          console.error('Failed to fetch access token', e);
          triggerReauth();
          return undefined;
        }
      },
      triggerReauth,
    );
  }, []);
}

export function useTokenGetter() {
  const logto = useLogto();
  const logtoRef = useRef(logto);
  useEffect(() => {
    logtoRef.current = logto;
  }, [logto]);

  return useMemo(
    () => async (): Promise<string | undefined> => {
      const { getAccessToken, isAuthenticated } = logtoRef.current;
      if (!isAuthenticated || !LOGTO_API_RESOURCE) return undefined;
      try {
        return await getAccessToken(LOGTO_API_RESOURCE);
      } catch (e) {
        console.error('Failed to fetch access token', e);
        return undefined;
      }
    },
    [],
  );
}
